/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  OnDestroy,
  Renderer2,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, Observable, Subject, Subscription, throwError} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap
} from 'rxjs/operators';

import cloneDeep from 'lodash-es/cloneDeep';

import * as moment from 'moment/moment';
import {Moment} from 'moment/moment';

import {FormCardFieldComponent} from './form-card-field/form-card-field.component';
import {FormCardRowComponent} from './form-card-row/form-card-row.component';

import {FieldType} from '../fields/fields.interfaces';
import {OrgEntity} from '../../../+organizations/org-interfaces';
import {DATE_FORMAT, DATE_MAX, DATE_MIN, ProfileMode} from '../../../app.constants';
import {ChatbotService} from '../../../chatbot/chatbot.service';
import {Destroyable} from '../../../destroyable.component';
import {EmpFile, Employee} from '../../../shared/employees/employees.model';
import {EmployeesService} from '../../../shared/employees/employees.service';
import {FileModel} from '../../../shared/utils/files';
import {ZoneType} from '../../employees/contract/contract.interfaces';
import {LocalizationService} from '../../localization/localization.service';
import {ModalService} from '../../modal';
import {isPresent, isValueConsideredAsEmpty} from '../../utils/lang';
import {EmpZoneDependencyUtils} from '../emp-zone-dependency.utils';
import {
  AiGeneratedValues,
  AiProcessingEvent,
  AiSourceOrTarget,
  BlockingError,
  CardElementErrors,
  EmpZoneDependency,
  ErrorMessage,
  FieldEventTrace,
  FormControlWithNativeElement,
  FormGeneratorError,
  FormGeneratorErrorKind,
  FormValues,
  GroupZoneDescriptor,
  GroupZoneType,
  PrefillRequestDTO,
  PrefillResponseDTO,
  Value,
  ZoneDescriptor,
  ZoneValues
} from '../form-generator.interfaces';
import {FormGeneratorService} from '../form-generator.service';
import {FormGeneratorUtils} from '../form-generator.utils';

const getGroupValueWhithouthDates = obj => {
  if (obj) {
    const newObject = {...obj};
    if (('beginDateControl' in obj) && ('endDateControl' in obj)) {
      delete newObject['beginDateControl'];
      delete newObject['endDateControl'];
    }
    return newObject;
  }
};

const PERSONAL_WORKCYCLE_ID = 'personal_workCycle_id';
const CAN_GENERATE_WITH_AI = 'CanGenerateWithAI';

const fillTypeOfItems = (refDateValue) =>
  Array(7).fill(0).map((v, index) => {
    const day: number = moment(refDateValue).add(index++, 'days').day();
    if (day === 0) {
      return 'SUN';
    } else if (day === 6) {
      return 'SAT';
    } else {
      return 'OPE';
    }
  });

const isFormInvalid = (control: AbstractControl, item: string): boolean =>
  control && control['controls'] && control['controls'][item]?.invalid;

/**
 * A card in a generated form component. Can span several columns
 */
@Component({
  selector: '[gp-form-card]',
  templateUrl: './form-card.component.html',
  styleUrls: ['./form-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormCardComponent extends Destroyable implements OnDestroy {

  public isFormInvalid = isFormInvalid;
  public fieldRows: Array<ComponentRef<FormCardRowComponent>> = [];
  public descriptor: GroupZoneDescriptor;
  public group: UntypedFormGroup;
  public groupCopy: UntypedFormGroup;
  public data: Array<ZoneValues | any>;
  public readOnly: boolean = false;
  public cliRegID: number = null;
  public beginDateValue: string;
  public endDateValue: string;
  public beginDateControl: UntypedFormControl = new UntypedFormControl('');
  public endDateControl: UntypedFormControl = new UntypedFormControl('');
  public minDate: Date = moment(DATE_MIN).startOf('day').toDate();
  public maxDate: Date = moment(DATE_MAX).startOf('day').toDate();
  public locale: string;
  public currency: string;
  public dataIndex: number;
  public loaded: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();

  public activatedGroup: string;
  public groupedCards: any;
  public form: UntypedFormGroup;
  public tabSheetID: string;
  public groupedZoneName: string;
  public dateFldsCls: string;
  public editMode: boolean = false;
  public anyHeritageRemoved = false;
  public blockingErrorModalIsLaunched = false;
  // NB : unlike formTable, which has one error validation "entity"
  // formCard has 2 :
  // - groupErrors: provided to the whole group: UntypedFormGroup with "setValidators"
  // - datesErrors: provided to beginDateControl and endDateControl
  // there are also errors in timelineValidation that are added to datesErrors
  public groupErrors: FormGeneratorError;
  public datesErrors: FormGeneratorError;

  public datesErrorText: string;
  public groupErrorText: string;

  public groupErrorMessages: ErrorMessage[] = [];
  public datesErrorMessages: ErrorMessage[] = [];

  public zoneErrors: FormGeneratorError;
  public zoneErrorMessages: ErrorMessage[] = [];

  public activeEntity: any;
  public isActiveCardSubscription: Subscription;
  public activeCardSubject: Subject<boolean>;
  public requiredZones: any[] = [];
  public requiredZonesKeys;
  public initialData: Array<ZoneValues | any>;
  public lastNonBlockingData: Array<ZoneValues | any>;
  public groupValueChangesSubscription: Subscription;

  public tabChangeSubscription: Subscription;
  public fieldFocusedSubscription: Subscription;
  public fieldBlurredSubscription: Subscription;
  public lastFieldFocused: FieldEventTrace;
  public lastFieldBlurred: FieldEventTrace;

  // Keep track from blocking errors at load and whenever there is a blur/focus
  public lastBlockingErrors: BlockingError[] = [];
  // Blocking errors after a field is blurred
  public newBlockingErrors: BlockingError[] = [];
  public isOnBoarding = false;
  public employee: Employee = null;
  public selectedOrg: OrgEntity = null;
  public aiGeneratedZones: string[] = [];
  public arg2GroupZonesForAiContext: string[] = [];
  public isGroupZoneLocked = false;
  public allData: FormValues;

  public showAiDisclaimer: boolean = false;

  private previousDate: { begindate: string, enddate: string } = {begindate: '', enddate: ''};

  @ViewChild('fieldRowsContainer', {read: ViewContainerRef, static: true})
  private rowsContainer: ViewContainerRef;
  private dialog: any;
  private isScrollAction: boolean = false;
  private cardCls: string;
  private removeClickOutsideListener: Function;

  private aiLockedControls = new Set<string>();

  private dependencySubscriptions: Subscription[] = [];

  constructor(private element: ElementRef,
              private formBuilder: UntypedFormBuilder,
              private componentFactoryResolver: ComponentFactoryResolver,
              private changeDetectorRef: ChangeDetectorRef,
              private localization: LocalizationService,
              private formGeneratorService: FormGeneratorService,
              private modalService: ModalService,
              private renderer: Renderer2,
              private translate: TranslateService,
              private chatbotService: ChatbotService,
              private employeeService: EmployeesService,
              private empZoneDependencyUtils: EmpZoneDependencyUtils
  ) {
    super();
  }

  public init(): any {

    if (this.descriptor.metadatas && this.descriptor.metadatas.readOnly
      || this.descriptor.readOnly
      || this.descriptor.groupZoneID === 'EndOfContract') {
      this.readOnly = true;
    }

    if (this.descriptor.groupZoneID.includes(':')) {
      this.groupedZoneName = this.descriptor.groupZoneID.split(':')[0];
      this.isActiveCardSubscription = this.formGeneratorService.isActiveCardChanged.asObservable()
        .pipe(takeUntil(this.activeCardSubject))
        .subscribe((data) => {
          this.groupedCards = data;
          this.changeDetectorRef.detectChanges();
        });

      if (!this.groupedCards[this.groupedZoneName]) {
        this.groupedCards[this.groupedZoneName] = {
          [this.descriptor.groupZoneID]: true
        };
      } else {
        if (!this.groupedCards[this.groupedZoneName][this.descriptor.groupZoneID]) {
          this.groupedCards[this.groupedZoneName][this.descriptor.groupZoneID] = false;
        }
      }
    }

    if (!this.descriptor.nbCol || this.descriptor.nbCol > 4) {
      this.descriptor.nbCol = 4;
    }

    this.cardCls = `col-md-${this.descriptor.nbCol * 3} col-xs-12`;
    this.descriptor.switchEditMode = new EventEmitter<boolean>();
    this.descriptor.card = this;
    this.descriptor.zones.forEach(zone => {
      if (zone.isRequired && zone.type !== ZoneType.AUTO_GENERATED) {
        this.requiredZones.push({zoneID: zone.zoneID, transId: zone.transId});
      }
    });
    // this.descriptor.paymentsModality = new EventEmitter<any>();

    if (this.isDated()) {
      const currentDate = moment().format(DATE_FORMAT);
      this.data.forEach((item: any) => {
        item.begindate = item.begindate || currentDate;
        item.enddate = item.enddate || currentDate;
      });

      switch (this.descriptor.nbCol) {
        case 1:
          this.dateFldsCls = 'col-md-12 col-xs-12';
          break;
        case 2:
          this.dateFldsCls = 'col-md-6 col-xs-12';
          break;
        case 3:
          this.dateFldsCls = 'col-md-4 col-xs-12';
          break;
        case 4:
          this.dateFldsCls = 'col-md-3 col-xs-12';
          break;
        default:
          this.dateFldsCls = 'col-md-12 col-xs-12';
          break;
      }

    }

    this.setAiGeneratedZones();

    this.load().then(group => {
      this.loaded.emit(group);
      this.loaded.complete();
      this.updateDatesValidity();
      this.initialData = cloneDeep(this.data);
      this.groupValidation();
      this.lastBlockingErrors = this.getBlockingErrors();
      this.changeDetectorRef.markForCheck();

      /* We check the valueChange of the form because when the property isCardInvalid is checked the form is
       undefined so once the form is created we emit the groupedCards so our isActiveCardSubscription intercept the
          value and recheck the validation of the groupedCards. We use isValueEmited because we want to do that only
          one time at the begin*/

      if (this.groupedCards && this.groupedCards[this.groupedZoneName]
        && Object.keys(this.groupedCards[this.groupedZoneName])[0] === this.descriptor.groupZoneID) {
        let isValueEmited = false;
        this.form.valueChanges
          .pipe(
            takeWhile(() => isValueEmited === false),
            debounceTime(100)
          ).subscribe(() => {
          this.formGeneratorService.isActiveCardChanged.emit(this.groupedCards);
          isValueEmited = true;
        });
      }

      this.group.valueChanges
        .pipe(
          takeUntil(this.componentDestroyed$),
          debounceTime(100)
        )
        .subscribe(() => {
          if (this.group.dirty && this.activeEntity.default) {
            Object.keys(this.group.controls).some((c: string) => {
              if (c !== 'beginDateControl' && c !== 'endDateControl' && this.group.get(c).dirty) {
                this.activeEntity.default = false;
                return true;
              }
            });
          }
        });
      this.translate.onLangChange.asObservable().subscribe(() => this.groupValidation());
    });
  }

  public getCurrentGlobalErrorGroup(): FormGeneratorError {
    return {...this.groupErrors, ...this.datesErrors, ...this.zoneErrors};
  }

  public getCurrentGlobalErrorMessageGroup(): ErrorMessage[] {
    return [...this.datesErrorMessages, ...this.groupErrorMessages, ...this.zoneErrorMessages];
  }

  public handleFocusEvent(event: FieldEventTrace) {
    if (!this.formGeneratorService.isSameFieldThanPreviousAndHasError(
      event,
      this.lastFieldFocused,
      this.newBlockingErrors,
      this.dataIndex)
    ) {
      if (this.blockingErrorModalIsLaunched) {
        const currentField: FormControlWithNativeElement =
          this.formGeneratorService.getZoneControlById(event.descriptor.zoneID, this.group);
        currentField.nativeElement.blur();
        return;
      }
      this.lastFieldFocused = cloneDeep(event);
      this.lastFieldFocused.dataIndex = this.dataIndex;
    }
    this.lastBlockingErrors = this.getBlockingErrors();
    this.changeDetectorRef.detectChanges();
  }

  public handleBlurEvent(event: FieldEventTrace) {
    if (this.blockingErrorModalIsLaunched) {
      return;
    }
    let openModal: boolean;

    // In case we did backToEditMode, we check if error is still there
    if (this.formGeneratorService.isSameFieldThanPreviousAndHasError(
      event,
      this.lastFieldBlurred,
      this.newBlockingErrors,
      this.dataIndex)
    ) {
      const previousErrorLength = this.newBlockingErrors.length;
      this.lastBlockingErrors = this.getBlockingErrors();
      this.changeDetectorRef.detectChanges();
      openModal = this.lastBlockingErrors.length === previousErrorLength;
    } else {
      // standard case, we check new blocking errors
      this.lastFieldBlurred = cloneDeep(event);
      this.lastFieldBlurred.dataIndex = this.dataIndex;

      this.newBlockingErrors = this.getBlockingErrors(true);
      this.lastBlockingErrors = this.getBlockingErrors();
      this.changeDetectorRef.detectChanges();
      openModal = !!this.newBlockingErrors.length;
    }
    if (openModal) {
      this.modalService.closeAllModals();
      this.changeDetectorRef.markForCheck();
      this.blockingErrorModalIsLaunched = true;
      this.modalService.openModalWithId('blockingErrorResolutionModal');
    }

  }

  public isLastFieldFocused(fieldToCheck: ZoneDescriptor): boolean {
    return FormGeneratorUtils.sanitizeZoneName(fieldToCheck.zoneID) === FormGeneratorUtils
      .sanitizeZoneName(this.lastFieldFocused.descriptor.zoneID);
  }

  public cancelChange(zoneDescriptor: ZoneDescriptor) {
    const lastEditedField: FormControlWithNativeElement =
      this.formGeneratorService.getZoneControlById(zoneDescriptor.zoneID, this.group);

    if (lastEditedField && this.isLastFieldFocused(zoneDescriptor)) {
      if (!this.editMode) {
        this.toggleCardEdit(true);
      }

      lastEditedField.setValue(this.lastFieldFocused.value);

      if (lastEditedField.value === this.getZoneLastNonBlockingValue(zoneDescriptor.zoneID)) {
        lastEditedField.markAsPristine();

        // Quick fix
        // We can have a "false" pristine when dates are generated by code ("see addDatedCardEntity")
        // Because when we do this, we mark the group as dirty (but the previous markAsPristine change this), but not the date input
        // todo: I guess the proper fix should be to markAsDirty the appropriate dates in addDatedCardEntity, then we could remove that fix
        this.updateGroupStatus();
      }
      this.toggleCardEdit(false);
      this.newBlockingErrors = [];
      this.blockingErrorModalIsLaunched = false;

    } else {
      throw new Error(`Last change on ${zoneDescriptor.zoneID} could not be canceled`);
    }
  }

  public getZoneLastNonBlockingValue(zoneId: string): any {
    return this.lastNonBlockingData[this.dataIndex].values[zoneId].value;
  }

  public backToEditMode(zoneDescriptor: ZoneDescriptor) {
    const lastEditedField: FormControlWithNativeElement =
      this.formGeneratorService.getZoneControlById(zoneDescriptor.zoneID, this.group);

    if (lastEditedField) {
      if (!this.editMode) {
        this.toggleCardEdit(true);
      }

      setTimeout(() => {
        if (lastEditedField.nativeElement) {
          lastEditedField.nativeElement.focus();
        } else {
          throw new Error(`lastEditField couldn't be focused because his nativeElement isn't accessible.
          Check if NativeElementInjectorDirective does work. ${lastEditedField}`);
        }
      }, 100);
    } else {
      throw new Error(`lastEditField couldn't be focused because the control couldn't be found`);
    }
    this.blockingErrorModalIsLaunched = false;
  }

  public ngOnDestroy() {
    if (this.groupValueChangesSubscription) {
      this.groupValueChangesSubscription.unsubscribe();
    }

    if (this.tabChangeSubscription) {
      this.tabChangeSubscription.unsubscribe();
      this.tabChangeSubscription = undefined;
    }
    this.removeFieldEventsListeners();
    super.ngOnDestroy();
    this.fieldRows.forEach(ref => {
      ref.destroy();
      ref = null;
    });
    this.fieldRows = [];
    if (this.removeClickOutsideListener) {
      this.removeClickOutsideListener();
    }
  }

  public isCardInvalid(): boolean {
    if (this.groupedCards && this.groupedCards[this.groupedZoneName]) {
      return Object.keys(this.groupedCards[this.groupedZoneName])
        .some(item => {
          const group: UntypedFormGroup = <UntypedFormGroup>this.form.controls[this.tabSheetID];

          return group && group.controls[item] && group.controls[item].invalid;
        });
    }

    return false;
  }

  public addClickOutsideListener() {
    this.removeClickOutsideListener = this.renderer.listen('document', 'click', (event) => {
      if (event.target && document.contains(event.target) && !this.element.nativeElement.contains(event.target)
      ) {
        this.toggleCardEdit(false);
      }
    });
  }

  public toggleCardEdit(isEditMode: boolean) {
    if (this.readOnly || this.isGroupZoneLocked) {
      return;
    }
    if (this.editMode !== isEditMode) {
      if (isEditMode) {
        this.addClickOutsideListener();
      } else if (this.editMode && this.removeClickOutsideListener) {
        this.removeClickOutsideListener();
      }
      this.editMode = isEditMode;
      this.descriptor.switchEditMode.next(this.editMode);
    }
  }

  public isCardVisible() {
    if (!this.groupedCards[this.groupedZoneName]) {
      return true;
    }

    return this.groupedCards[this.groupedZoneName][this.descriptor.groupZoneID];
  }

  private setFieldEventsListeners() {
    // Store each time a field is focused
    if (!this.fieldFocusedSubscription) {
      this.fieldFocusedSubscription = this.formGeneratorService.fieldFocused$
        .pipe(
          filter(event => event !== null),
          filter(event => this.formGeneratorService.filterEventByZoneGroup(event, this.descriptor, true))
        )
        .subscribe((event: FieldEventTrace) => {
          this.handleFocusEvent(event);
        });
    }

    // Store each time a field is blurred
    if (!this.fieldBlurredSubscription) {
      this.fieldBlurredSubscription = this.formGeneratorService.fieldBlurred$
        .pipe(
          filter(event => event !== null),
          filter(event => this.formGeneratorService.filterEventByZoneGroup(event, this.descriptor, true))
        )
        .subscribe((event: FieldEventTrace) => {
          this.handleBlurEvent(event);
        });
    }
  }

  private removeFieldEventsListeners() {
    if (this.fieldFocusedSubscription) {
      this.fieldFocusedSubscription.unsubscribe();
      this.fieldFocusedSubscription = undefined;
    }
    if (this.fieldBlurredSubscription) {
      this.fieldBlurredSubscription.unsubscribe();
      this.fieldBlurredSubscription = undefined;
    }
  }

  private setTabChangeListener() {
    if (!this.tabChangeSubscription) {
      this.tabChangeSubscription = this.formGeneratorService.currentTab$
        .pipe(
          filter(event => event !== null)
        )
        .subscribe((tabId: string) => {
          this.handleTabChange(tabId);
        });
    }
  }

  // Avoid to listen events in hidden tabs
  private handleTabChange(tabId: string) {
    if (this.tabSheetID === tabId) {
      this.setFieldEventsListeners();
    } else {
      this.removeFieldEventsListeners();
    }
  }

  private updateGroupStatus() {
    if (this.isDataChanged()) {
      this.group.markAsDirty();
    } else {
      this.group.markAsPristine();
    }
    this.changeDetectorRef.markForCheck();
  }

  private getBlockingErrors(compareToPreviousState?: boolean): BlockingError[] {
    return this.formGeneratorService.getBlockingErrors(
      this.getCurrentGlobalErrorGroup(),
      this.getCurrentGlobalErrorMessageGroup(),
      this.tabSheetID,
      this.descriptor.transId,
      compareToPreviousState ? this.lastBlockingErrors : undefined
    );
  }

  public onScrollClick(event: MouseEvent) {
    const target = (<Element>event.target).closest('.scroll'),
      height = target.getBoundingClientRect().height,
      clickY = event.clientY - target.getBoundingClientRect().top,
      ratio = clickY / height,
      slice = 1 / this.data.length;

    this.dataIndex = this.data.findIndex((data: any, idx: number) => ratio > idx * slice && ratio < (idx + 1) * slice);
    this.isScrollAction = true;
    this.loadFields()
      .then(() => {
        this.descriptor?.switchEditMode?.next(this.editMode);
        this.changeDetectorRef.detectChanges();
      })
      .finally(() => {
        this.isScrollAction = false;
      });
  }

  public addDatedCardEntity(isCopy: boolean = false) {
    if (this.newBlockingErrors.length) {
      return;
    }
    if (this.isAddAllowed() && this.data.length > 0) {
      const activeEntity = this.data[0];
      if (activeEntity.enddate === DATE_MAX) {
        if (moment(activeEntity.begindate).diff(moment(), 'days') >= 0) {
          activeEntity.enddate = moment(activeEntity.begindate).add(1, 'days').format(DATE_FORMAT);
        } else {
          activeEntity.enddate = moment().add(-1, 'days').format(DATE_FORMAT);
        }
      }
      const emptyData = this.formGeneratorService.createEmptyData(this.descriptor, undefined, false)[0];
      emptyData.begindate = moment(activeEntity.enddate).add(1, 'days').format(DATE_FORMAT);

      if (isCopy) {
        Object.keys(emptyData.values).forEach(key => {
          if (activeEntity.values[key].type === FieldType.FILE) {
            return;
          }
          emptyData.values[key].value = activeEntity.values[key].value;
          if (activeEntity.values[key].transId) {
            emptyData.values[key].transId = activeEntity.values[key].transId;
          }
        });
      }
      this.data.unshift(emptyData);

      this.group.markAsDirty();
      this.load();
    }
  }

  public deleteCardEntity() {

    if (this.newBlockingErrors.length) {
      return;
    }

    if (this.isDated()) {
      this.deleteDatedCardEntity();
    } else {
      this.deleteNonDatedCardEntity();
    }
  }

  public displayDate(date: string): string {
    if (date === DATE_MAX || date === DATE_MIN) {
      return '';
    }
    return moment(date).format(this.localization.dateFormat);
  }

  public groupZoneHasReadOnlyZone() {
    return this.descriptor?.zones?.some(zone => zone.readOnly);
  }

  public updateDate(newValue: string, dateType: string) {
    if (this.group) {
      this.previousDate.begindate = this.activeEntity.begindate;
      this.previousDate.enddate = this.activeEntity.enddate;

      if (newValue === '') {
        dateType === 'enddate' ? this.setMaxEndDate(false) : this.setMinBeginDate(false);
        return;
      }
      const isValidValue = moment(newValue, DATE_FORMAT, true).isValid();
      if (isValidValue) {
        if (moment(newValue).isAfter(DATE_MAX) || moment(newValue).isBefore(DATE_MIN)) {
          dateType === 'enddate' ? this.setMaxEndDate(true) : this.setMinBeginDate(true);
          return;
        }
      }

      this.activeEntity[dateType] = newValue;
      this.updateDatesValidity();

      if (isValidValue && !this.isScrollAction) {
        if (this.descriptor.groupZoneType === GroupZoneType.UNIQUE_MANDATORY && !this.dialog && this.editMode) {
          if (this.datesErrors && !this.datesErrors.hasOwnProperty(FormGeneratorErrorKind.BEGIN_AFTER_END)) {
            this.fixDates();
          }
        }
      }
    }
  }

  public shouldShowTrashIcon(): boolean {
    return this.editMode
      && (this.descriptor?.isDated || !this.groupZoneHasReadOnlyZone())
      && this.descriptor?.groupZoneID !== 'BankingDetails:PrimaryAccount';
  }

  public hasCalculation(): boolean {
    return this.fieldRows.some(row =>
      row.instance.fields.some(field =>
        field.instance.canCalculate || field.instance.forceCalculate)
    );
  }

  private isDated(): boolean {
    return this.descriptor && this.descriptor.isDated;
  }

  private getBeginDate(): string {
    if (!this.activeEntity) {
      return moment().format(DATE_FORMAT);
    }

    if (moment(DATE_MIN).isAfter(moment(this.activeEntity.begindate))) {
      return DATE_MIN;
    }

    return this.activeEntity.begindate;
  }

  private setMinBeginDate(notification: boolean = false) {
    if (notification) {
      this.modalService.dialogFromData('begin-date-before-min', this.descriptor.transId)
        .then(() => {
          this.setMinBeginDate(false);
        });

      return;
    }

    setTimeout(() => {
      this.beginDateValue = DATE_MIN;
      this.updateDatesValidity();
      this.changeDetectorRef.markForCheck();
    });
  }

  private getEndDate(): string {
    if (!this.activeEntity) {
      return moment().format(DATE_FORMAT);
    }

    if (moment(DATE_MAX).isBefore(moment(this.activeEntity.enddate))) {
      return DATE_MAX;
    }

    return this.activeEntity.enddate;
  }

  private setMaxEndDate(notification: boolean = false) {
    if (notification) {
      this.modalService.dialogFromData('end-date-after-max', this.descriptor.transId)
        .then(() => {
          this.setMaxEndDate(false);
        });

      return;
    }

    setTimeout(() => {
      this.endDateValue = DATE_MAX;
      this.updateDatesValidity();
      this.changeDetectorRef.markForCheck();
    }, 0);
  }

  private updateDatesValidity() {
    if (this.descriptor.isDated) {
      this.beginDateControl.updateValueAndValidity();
      this.endDateControl.updateValueAndValidity();
    }
  }

  private datesValidation(skipTimelineValidation: boolean = false): any {
    this.datesErrors = {};
    this.datesErrorText = null;
    this.datesErrorMessages = [];

    if (!isPresent(this.activeEntity) || !this.descriptor.isDated) {
      return null;
    }

    if (this.activeEntity.begindate && this.activeEntity.enddate) {
      const beginDate = moment(this.activeEntity.begindate, DATE_FORMAT, true),
        endDate = moment(this.activeEntity.enddate, DATE_FORMAT, true);
      if (beginDate.isAfter(endDate)) {
        this.pushErrorToErrorGroup(this.datesErrors, FormGeneratorErrorKind.BEGIN_AFTER_END, true);
      } else if (!skipTimelineValidation) {
        Object.assign(this.datesErrors, this.timeLineValidation());
      }
    }

    Object.keys(this.datesErrors).forEach(key => {
      if (!this.datesErrors[key]) {
        delete this.datesErrors[key];
      } else {
        this.datesErrorMessages.push({
          text: key,
          additionalValues: this.datesErrors[key].additionalValues
        });
      }
    });

    if (!Object.keys(this.datesErrors).length) {
      this.datesErrors = null;
    } else {
      this.datesErrorText = Object.keys(this.datesErrors)[0];
    }

    return this.datesErrors;
  }

  private timeLineValidation(): FormGeneratorError {
    const errors: FormGeneratorError = {};
    if (!this.data) {
      return errors;
    }

    if (this.descriptor.groupZoneType === GroupZoneType.UNIQUE_MANDATORY) {

      const check = this.formGeneratorService.getGapsOverlapsBoundaries(this.data);

      if (check.gapsToBegin > 0) {
        this.pushErrorToErrorGroup(errors, FormGeneratorErrorKind.DATED_BLOCK_FIRST_OCCURENCE_BEGIN_DATE_ETERNITY);
      }
      if (check.gapsToEnd > 0) {
        this.pushErrorToErrorGroup(errors, FormGeneratorErrorKind.DATED_BLOCK_LAST_OCCURENCE_END_DATE_ETERNITY);
      }
      if (check.gaps > 0) {
        this.pushErrorToErrorGroup(errors, FormGeneratorErrorKind.GAPS);
      }
      if (check.overlaps > 0) {
        this.pushErrorToErrorGroup(errors, FormGeneratorErrorKind.OVERLAPS);
      }
    }

    return errors;
  }

  private fixDates() {
    if (this.data.last() === this.activeEntity) {
      if (this.activeEntity.begindate !== DATE_MIN) {
        this.dialog = this.modalService.dialogFromData(
          'dated-block-first-occurence-begindate-eternity',
          this.descriptor.transId
        ).then((choice: string) => {
          if (choice === 'add-minus-eternity') {
            const emptyData = this.formGeneratorService.createEmptyData(this.descriptor)[0];
            emptyData.enddate = moment(this.activeEntity.begindate).add(-1, 'days').format(DATE_FORMAT);
            emptyData.begindate = DATE_MIN;
            this.data.unshift(emptyData);
            this.dataIndex++;
            this.loadFields();
          } else {
            this.activeEntity.begindate = this.beginDateValue = this.previousDate.begindate;
            this.activeEntity.enddate = this.endDateValue = this.previousDate.enddate;
          }
          this.dialog = null;
        });
      }
    }
    if (this.data[0] === this.activeEntity) {
      if (this.activeEntity.enddate !== DATE_MAX) {
        this.dialog = this.modalService.dialogFromData(
          'dated-block-last-occurence-enddate-eternity',
          this.descriptor.transId
        ).then((choice: string) => {
          if (choice === 'add-plus-eternity') {
            const emptyData = this.formGeneratorService.createEmptyData(this.descriptor)[0];
            emptyData.enddate = DATE_MAX;
            emptyData.begindate = moment(this.activeEntity.enddate).add(1, 'days').format(DATE_FORMAT);
            this.data.push(emptyData);
            this.dataIndex = 0;
            this.loadFields();
          } else {
            this.activeEntity.begindate = this.beginDateValue = this.previousDate.begindate;
            this.activeEntity.enddate = this.endDateValue = this.previousDate.enddate;
          }
          this.dialog = null;
        });
      }
    }

    const prevEntity = this.data[this.activeEntity.dataIndex + 1];
    const nextEntity = this.data[this.activeEntity.dataIndex - 1];

    if (prevEntity && moment(this.activeEntity.begindate).diff(moment(prevEntity.begindate), 'days') > 1) {
      prevEntity.enddate = moment(this.activeEntity.begindate).add(-1, 'days').format(DATE_FORMAT);
    }
    if (nextEntity && moment(nextEntity.enddate).diff(moment(this.activeEntity.enddate), 'days') > 1) {
      nextEntity.begindate = moment(this.activeEntity.enddate).add(1, 'days').format(DATE_FORMAT);
    }

    this.datesValidation();
    if (
      this.descriptor.groupZoneType === GroupZoneType.UNIQUE_MANDATORY
      && this.datesErrors
      && this.datesErrors.hasOwnProperty(FormGeneratorErrorKind.OVERLAPS)
      && !this.dialog
    ) {
      this.handleDynamicConfirmModalDialog();
    }

    this.updateDatesValidity();
  }

  private handleDynamicConfirmModalDialog(): void {
    const blockName = this.descriptor.transId;
    this.dialog = this.modalService.dynamicConfirmModal({
      title: 'DIALOG.overwrite_block_occurrence',
      text: this.translate.instant('DIALOG.overwrite_block_occurrence?', {blockName}),
      confirmButton: 'FORMS.overwrite_occurrences',
      cancelButton: 'FORMS.cancel'
    }).then((choice: boolean) => {
      if (!choice) {
        this.activeEntity.begindate = this.beginDateValue = this.previousDate.begindate;
        this.activeEntity.enddate = this.endDateValue = this.previousDate.enddate;
      } else {
        this.data = this.data.filter((entity: any) => {
          if (entity === this.activeEntity) {
            return true;
          }

          const begindate: Moment = moment(this.activeEntity.begindate);
          const enddate: Moment = moment(this.activeEntity.enddate);

          if (moment(entity.begindate).isBefore(begindate) && moment(entity.enddate).isSameOrAfter(begindate)) {
            entity.enddate = moment(this.activeEntity.begindate).add(-1, 'days').format(DATE_FORMAT);
          }

          if (moment(entity.begindate).isSameOrBefore(enddate) && moment(entity.enddate).isAfter(enddate)) {
            entity.begindate = moment(this.activeEntity.enddate).add(1, 'days').format(DATE_FORMAT);
          }

          return !moment(entity.begindate).isBetween(begindate, enddate)
            && !moment(entity.enddate).isBetween(begindate, enddate);
        });
        this.dataIndex = this.data.indexOf(this.activeEntity);
        this.loadFields();
      }
      this.dialog = null;
    });
  }

  private groupValidation() {

    this.groupErrors = {};
    this.groupErrorText = null;
    this.groupErrorMessages = [];
    let emptyRequiredZones = [];
    this.requiredZonesKeys = {};

    if (this.descriptor) {

      // When we change a value in the group values the validator function groupValidation is checked
      // but at this moment this.data is not updated with the new value so here we do the update

      if (this.data && this.activeEntity) {
        if (this.descriptor.isDated) {
          this.data[this.activeEntity.dataIndex].begindate = this.group.value.beginDateControl;
          this.data[this.activeEntity.dataIndex].enddate = this.group.value.endDateControl;
        }
        Object.entries(getGroupValueWhithouthDates(this.group.value)).forEach(zone => {
          const zoneName = zone[0].replace('§', '.');
          const value = zone[1];
          const isTransIdList = this.descriptor.zones.find(z => z.zoneID === zoneName)
            ?.metadatas
            ?.transIdList;

          if (this.descriptor.isDated
            && this.data[this.activeEntity.dataIndex].values.hasOwnProperty(zoneName)
            && !isTransIdList) {
            this.data[this.activeEntity.dataIndex].values[zoneName].value = value;
          } else if (!this.descriptor.isDated
            && this.data[0].values.hasOwnProperty(zoneName)
            && !isTransIdList) {
            this.data[0].values[zoneName].value = value;
          }
        });

        // when we have 2+ elements in a card we have to check all zone errors for all elements not only for the active
        // one so here we do that check

        this.checkZoneErrorsInNonActiveEntities();
      }

      this.checkWorkCycleForErrors();

      if (this.formGeneratorService.isGroupZoneMandatory(this.descriptor.groupZoneType)) {

        if (this.isUndefinedDataEmpty()) {
          this.pushErrorToErrorGroup(this.groupErrors, FormGeneratorErrorKind.ONE_LINE_REQUIRED);
        }

      } else if (this.group && this.noDataForThisGroup()
        && !this.allCardsValuesAreEmpty()
        && this.requiredZones.length > 0) {

        this.requiredZones.forEach(key => {
          this.data.forEach(data => {
            if (data.values.hasOwnProperty(key.zoneID) && isValueConsideredAsEmpty(data.values[key.zoneID].value)
              && !emptyRequiredZones.includes(key.transId)) {
              emptyRequiredZones.push(key.transId);
            }
          });
        });

        if (emptyRequiredZones.length > 0) {
          this.translate.get(emptyRequiredZones).subscribe(value => {
            emptyRequiredZones = Object.values(value);
          });
          if (emptyRequiredZones.length === 1) {
            this.pushErrorToErrorGroup(this.groupErrors, FormGeneratorErrorKind.KEY_REQUIRED, true, {key: emptyRequiredZones[0]});
            this.requiredZonesKeys = {key: emptyRequiredZones[0]};
          } else {
            this.pushErrorToErrorGroup(this.groupErrors, FormGeneratorErrorKind.KEYS_REQUIRED, true, {
              keys: emptyRequiredZones.join(', ')
            });
            this.requiredZonesKeys = {keys: emptyRequiredZones.join(', ')};
          }
        }
      }

      if (!this.descriptor.isDated) {
        this.checkNonDatedGroupZone();
      }

    }

    this.checkZoneErrors();

    Object.keys(this.groupErrors).forEach(key => {
      if (!this.groupErrors[key]) {
        delete this.groupErrors[key];
      } else {
        this.groupErrorMessages.push({
          text: key,
          additionalValues: this.groupErrors[key].additionalValues
        });
      }
    });

    if (!Object.keys(this.groupErrors).length) {
      this.groupErrors = null;
    } else {
      this.groupErrorText = Object.keys(this.groupErrors)[0];
    }

    return this.groupErrors;
  }

  private checkNonDatedGroupZone() {
    const entitiesNumber = this.data.length;
    if (entitiesNumber > 1) {

      let onlyInheritedValues = false;
      if (this.group?.value) {
        onlyInheritedValues = Object.keys(this.group.value).every(key => {
          const foundZone = this.descriptor.zones.find(item => item.zoneID === key);
          return foundZone?.repInheritedValue;
        });
      }

      if(!onlyInheritedValues || this.anyHeritageRemoved) {
        this.pushErrorToErrorGroup(
          this.groupErrors,
          FormGeneratorErrorKind.ONLY_ONE_LINE_ALLOWED,
          true,
          {key: entitiesNumber}
        );
      }
    } else if (entitiesNumber === 1) {
      // Those are dates validation but we can put it in the datesValidation function
      // as it is bound to form control and we don't have it for non dated blocks
      if (!moment(this.data[0].begindate).isSame(moment(DATE_MIN))) {
        this.pushErrorToErrorGroup(this.groupErrors, FormGeneratorErrorKind.NON_DATED_BLOCK_BEGIN_DATE_ETERNITY, true);
      }
      if (!moment(this.data[0].enddate).isSame(moment(DATE_MAX))) {
        this.pushErrorToErrorGroup(this.groupErrors, FormGeneratorErrorKind.NON_DATED_BLOCK_END_DATE_ETERNITY, true);
      }
    }
    this.changeDetectorRef.markForCheck();
  }

  // Loop on zones to store errors
  private checkZoneErrors() {
    this.zoneErrors = {};
    this.zoneErrorMessages = [];

    if (this.group && this.group.controls) {
      Object.keys(this.group.controls).forEach(controlKey => {
        if (this.group.controls[controlKey].errors) {
          Object.keys(this.group.controls[controlKey].errors).forEach(errorKey => {
            if (this.group.controls[controlKey].errors[errorKey] === true) {
              const formattedErrorKey = this.formGeneratorService.formatErrorKey(errorKey);
              const zoneTransId = this.formGeneratorService.getTransIdFromZoneId(controlKey, this.descriptor);
              this.pushZoneError(formattedErrorKey, zoneTransId);
            }
          });
        }
      });
    }

    Object.keys(this.zoneErrors).forEach(key => {
      if (!this.zoneErrors[key].status) {
        delete this.zoneErrors[key];
      } else {
        this.zoneErrorMessages.push({
          text: key,
          additionalValues: this.zoneErrors[key].additionalValues,
          zone: this.zoneErrors[key].zone
        });
      }
    });
  }

  private checkWorkCycleForErrors() {
    const WORK_CYCLE_GROUP_NAME = 'WorkCycle';
    const PERSONAL_WORK_CYCLE_LABEL = 'personal';
    this.data.forEach(data => {
      if (data?.groupName === WORK_CYCLE_GROUP_NAME && data.values?.workCycleType?.value === PERSONAL_WORK_CYCLE_LABEL) {
        const refDate = moment(data?.values?.refDate?.value);
        const endDate = moment(data?.values?.endcycledate?.value);
        const workCycleEndDateBeforeBeginDateError = this.groupErrors[FormGeneratorErrorKind.WORK_CYCLE_END_DATE_BEFORE_BEGIN_DATE];
        if (refDate.isValid() && endDate.isValid() && (endDate.diff(refDate, 'days')) < 0 && !workCycleEndDateBeforeBeginDateError) {
          this.pushErrorToErrorGroup(this.groupErrors, FormGeneratorErrorKind.WORK_CYCLE_END_DATE_BEFORE_BEGIN_DATE);
        }
      }
    });
  }

  private pushErrorToErrorGroup(errorGroup: FormGeneratorError, errorKey: string, status: boolean = true, additionalValues: any = {}) {
    errorGroup[errorKey] = this.formGeneratorService.createError(errorKey, status, additionalValues);
  }

  private pushZoneError(errorKey: string, zone: string) {
    // add prefix because we can have several zones with same errorKey
    // TODO: refactor it with a more convenient data model
    this.zoneErrors[`${zone}/${errorKey}`] = this.formGeneratorService.createError(errorKey, true, {}, zone);
  }

  private load(): Promise<UntypedFormGroup> {
    this.dataIndex = this.getDataIndex();
    return this.loadFields();
  }

  private copyControlStructure(source: AbstractControl): UntypedFormControl {
    return new UntypedFormControl(source.value, source.validator, source.asyncValidator);
  }

  private copyGroupStructure(group: UntypedFormGroup): UntypedFormGroup {
    const copy: UntypedFormGroup = this.formBuilder.group({});
    Object.keys(group.controls).forEach((key: string) =>
      copy.setControl(key, this.copyControlStructure(group.controls[key])));
    return copy;
  }

  private getDataIndex(): number {
    let dateIndex: number = 0;
    if (this.isDated &&
      (this.descriptor.groupZoneType === 'UNIQUE_MANDATORY'
        || this.descriptor.groupZoneType === 'UNIQUE_NON_MANDATORY')) {
      if (this.descriptor.metadatas && this.descriptor.metadatas.contractDates && this.data && this.data.length > 0) {
        const current = moment();
        const contractBeginDate = moment(this.descriptor.metadatas.contractDates.beginDate);
        const contractEndDate = moment(this.descriptor.metadatas.contractDates.endDate);
        if (current.isSameOrAfter(contractBeginDate) && current.isSameOrBefore(contractEndDate)) {
          // display occurence which is valid for current
          dateIndex = this.data.findIndex(data =>
            current.isSameOrAfter(moment(data.begindate)) && current.isSameOrBefore(moment(data.enddate)));
        } else if (current.isBefore(contractBeginDate)) {
          // display occurence which is valid for contractBeginDate
          dateIndex = this.data.findIndex(data =>
            contractBeginDate.isSameOrAfter(moment(data.begindate)) &&
            contractBeginDate.isSameOrBefore(moment(data.enddate)));
        } else if (current.isAfter(contractEndDate)) {
          // display occurence which is valid for contractEndDate
          dateIndex = this.data.findIndex(data =>
            contractEndDate.isSameOrAfter(moment(data.begindate)) &&
            contractEndDate.isSameOrBefore(moment(data.enddate)));
        }
      }
    }
    if (dateIndex < 0) {
      return 0;
    }
    return dateIndex;
  }

  private loadFields(): Promise<UntypedFormGroup> {
    if (this.descriptor.isDated) {
      this.data = this.formGeneratorService.sortDated(this.data);
    }

    this.activeEntity = this.data[this.dataIndex] || this.data[0];
    // Remove existing rows if any
    this.ngOnDestroy();

    const columns = this.descriptor.nbCol;
    const rows: Array<Array<ZoneDescriptor>> =
      this.descriptor.zones
        .sort((zoneA: ZoneDescriptor, zoneB: ZoneDescriptor) => zoneA.displayorder - zoneB.displayorder)
        .reduce((result: Array<Array<ZoneDescriptor>>, item: ZoneDescriptor) => {

          if (item.hidden) {
            item.nbColumns = 0;
          }
          const lastRowItems =
            result[result.length - 1] === undefined ? 0 :
              result[result.length - 1].reduce((a: number, zone: ZoneDescriptor) => a + zone.nbColumns, 0);

          if (result.length === 0 || columns < item.nbColumns + lastRowItems) {
            // If first row or if this item will make the current row overflow , we create a new row
            result.push([]);
          }
          // Push card in current row
          result[result.length - 1].push(item);
          return result;
        }, []);

    // Load field components
    const promisesOfLoadedObservables: Array<Promise<FormCardFieldComponent[]>> =
      rows.map((fields: Array<ZoneDescriptor>) => {
        const rowRef: ComponentRef<FormCardRowComponent> = this.rowsContainer.createComponent(
          this.componentFactoryResolver.resolveComponentFactory(FormCardRowComponent));

        this.changeDetectorRef.markForCheck();
        const row: FormCardRowComponent = rowRef.instance;
        this.fieldRows.push(rowRef);
        row.cardDescriptor = this.descriptor;
        row.fieldDescriptors = fields;
        row.data = this.data;
        row.dataIndex = this.dataIndex;
        row.currency = this.currency;
        row.locale = this.locale;
        row.editMode = this.editMode;
        row.isOnBoarding = this.isOnBoarding;
        row.employeeId = this.employee?.id;
        row.generateWithAiUsingFile = this.onGenerateWithAiUsingFile.bind(this);

        return (<any>row.loaded).toPromise();
      });

    return Promise.all(promisesOfLoadedObservables).then(fields => {
      const all: Array<FormCardFieldComponent> = [].concat.apply([], fields);

      if (this.descriptor.internalType === 'BANKING_DETAILS') {
        this.checkPaymentsModality(all);
      }
      if (this.descriptor.groupZoneID === 'WorkCycle') {
        this.checkWorkCycle(all);
      }

      const group: any = all.reduce((result, item) => {

        if (item.isHeritageRemoved) {
          this.anyHeritageRemoved = true;
        }
        // Replace the zoneID '.' (dot) of linked entities (like cliEmp.firstName) because it breaks angular
        result[FormGeneratorUtils.sanitizeZoneName(item.descriptor.zoneID)] = item.control;
        return result;
      }, {});

      const isGroupDirty: boolean = this.group && this.group.dirty;

      if (this.isDated()) {
        const isDatesDirty: boolean = (this.beginDateControl.value && this.beginDateControl.dirty)
          || (this.endDateControl.value && this.endDateControl.dirty);

        this.beginDateValue = this.getBeginDate();
        this.endDateValue = this.getEndDate();
        this.beginDateControl.setValue(this.beginDateValue);
        this.endDateControl.setValue(this.endDateValue);
        this.beginDateControl.setValidators(this.datesValidation.bind(this, false));
        this.endDateControl.setValidators(this.datesValidation.bind(this, false));
        if (!isDatesDirty) {
          this.beginDateControl.markAsPristine();
          this.endDateControl.markAsPristine();
        }
        group.beginDateControl = this.beginDateControl;
        group.endDateControl = this.endDateControl;
      }

      if (this.group) {
        Object.keys(this.group.controls).forEach((key: string) => this.group.removeControl(key));
        Object.keys(group).forEach(key => this.group.setControl(key, group[key]));

        Object.keys(this.group.controls).forEach((key: string) => {
          if (!this.groupCopy.controls.hasOwnProperty(key)) {
            this.groupCopy.setControl(key, this.copyControlStructure(this.group.controls[key]));
          }
        });

        if (isGroupDirty) {
          this.group.markAsDirty();
        } else {
          this.group.markAsPristine();
        }
      } else {
        this.group = this.formBuilder.group(group);
        this.groupCopy = this.copyGroupStructure(this.group);
        this.group.setValidators(this.groupValidation.bind(this));
      }

      this.changeDetectorRef.markForCheck();


      this.groupValueChangesSubscription = this.group.valueChanges.pipe(
        tap(() => {
          if (this.group.dirty) {
            if (!this.isDataChanged()) {
              this.group.markAsPristine();
              this.changeDetectorRef.markForCheck();
            }
          }
        }),
        debounceTime(400),
        tap(() => {
          if (this.group.dirty) {
            fields.forEach((fieldRow) => {
              fieldRow.forEach((field) => {
                field.changeDetectorRef.detectChanges();
              });
            });
          }
        })
      )
        .subscribe();

      this.setTabChangeListener();

      // todo: If new entity add blockingError... ?
      this.newBlockingErrors = this.getBlockingErrors(true);
      if (this.newBlockingErrors.length) {
        // ...
      } else {
        this.lastNonBlockingData = cloneDeep(this.data);
      }

      this.registerDependencies();
      this.group?.updateValueAndValidity();
      return this.group;
    });
  }

  private isAddAllowed(): boolean {
    return !this.datesErrors || !this.datesErrors.hasOwnProperty(FormGeneratorErrorKind.OVERLAPS);
  }

  private deleteEntityHandler(): Promise<any> {
    if (this.descriptor.groupZoneType === 'UNIQUE_MANDATORY') {
      if (this.data[0] === this.activeEntity) {
        this.data[1].enddate = DATE_MAX;
      } else if (this.data.last() === this.activeEntity) {
        this.data[this.activeEntity.dataIndex - 1].begindate = DATE_MIN;
      } else {
        return this.modalService.dialogFromData(
          'dated-block-delete-handler',
          this.descriptor.transId
        ).then((choice: string) => {
          if (choice === 'change-previous') {
            this.data[this.activeEntity.dataIndex + 1].enddate = this.activeEntity.enddate;
            this.endDateControl.markAsDirty();
          } else if (choice === 'change-next') {
            this.data[this.activeEntity.dataIndex - 1].begindate = this.activeEntity.begindate;
            this.beginDateControl.markAsDirty();
          }
        });
      }
    }

    return Promise.resolve();
  }

  public activateGroup(group: string, event: MouseEvent) {
    event.stopImmediatePropagation();

    if (!this.groupedCards[this.groupedZoneName][group]) {
      this.groupedCards[this.groupedZoneName][group] = true;
      this.groupedCards[this.groupedZoneName][this.descriptor.groupZoneID] = false;
      this.activatedGroup = group;
      this.formGeneratorService.isActiveCardChanged.emit(this.groupedCards);
    }
  }

  private checkPaymentsModality(all: Array<FormCardFieldComponent>) {
    const paymentsModalityField = all.find(f => f.descriptor.zoneID.includes('.paymentModality'));

    paymentsModalityField.descriptor.metadatas.items = paymentsModalityField.descriptor.metadatas.items
      .filter(item => this.descriptor.metadatas.descriptors.has(item.value));

    paymentsModalityField.control.valueChanges
      .pipe(
        // takeUntil(this.componentDestroyed$),
        startWith(paymentsModalityField.control.value),
        distinctUntilChanged()
      )
      .subscribe((value: string) => {
        const zones: ZoneDescriptor[] = this.descriptor.metadatas.descriptors.get(value || 'default').zones;

        if (this.descriptor.zones === zones) {
          return;
        }

        this.descriptor.zones = zones;
        const emptyData = this.formGeneratorService.createEmptyData(this.descriptor)[0];
        this.descriptor.zones.forEach((z: ZoneDescriptor) => {
          if (this.data[this.dataIndex].values[z.zoneID]) {
            emptyData.values[z.zoneID] = this.data[this.dataIndex].values[z.zoneID];
          }
        });

        if (value !== 'default') {
          emptyData.values[paymentsModalityField.descriptor.zoneID].value = value;
        }

        emptyData.begindate = this.data[this.dataIndex].begindate;
        emptyData.enddate = this.data[this.dataIndex].enddate;
        emptyData.id = this.data[this.dataIndex].id;
        emptyData.isDated = this.data[this.dataIndex].isDated;

        this.data[this.dataIndex] = emptyData;

        this.loadFields();
      });
  }

  private checkWorkCycle(all: Array<FormCardFieldComponent>) {
    const workCycleField = all.find(f => f.descriptor.zoneID === 'WorkCycle');
    const tableField = all.find(f => f.descriptor.zoneID === 'workCycleTable');
    const refDateField = all.find(f => f.descriptor.zoneID === 'refDate');
    const workCycleType = all.find(f => f.descriptor.zoneID === 'workCycleType');
    const workCycleTableType = all.find(f => f.descriptor.zoneID === 'workCycleTableType');
    const endCycleField = all.find(f => f.descriptor.zoneID === 'endcycledate');

    // remove personal_workCycle_id if exists
    this.descriptor.metadatas.items = this.descriptor.metadatas.items
      .filter((c: any) => c.cycleId !== PERSONAL_WORKCYCLE_ID);

    // create new personal_workCycle_id
    const personalCycle = {
      cycleId: PERSONAL_WORKCYCLE_ID,
      items: [],
      refdate: '',
      endDate: ''
    };
    this.descriptor.metadatas.items.push(personalCycle);
    const cycle = this.descriptor.metadatas.items.find((item: any) => item.cycleId === workCycleField.value.value);
    // if it's saved personal workcycle ID is not 'personal_workCycle_id'
    if ((!cycle && workCycleField.value.value) || (cycle && cycle.cycleId === PERSONAL_WORKCYCLE_ID)) {
      workCycleField.control.setValue(PERSONAL_WORKCYCLE_ID);
      workCycleField.value.value = workCycleField.control.value;
      Object.assign(personalCycle, {
        items: tableField.value.value || [],
        refdate: refDateField.value.value || ''
      });
    }

    tableField.control.valueChanges
      .pipe(
        takeUntil(tableField.componentDestroyed$)
      )
      .subscribe(value => {
        if (workCycleField.value.value === PERSONAL_WORKCYCLE_ID) {
          personalCycle.items = value || [];
        }
      });

    if (endCycleField) {
      endCycleField.control.valueChanges
        .pipe(
          tap(endDateValue => {
            if (workCycleField.value.value === PERSONAL_WORKCYCLE_ID) {
              personalCycle.endDate = endDateValue || '';
              workCycleTableType.control.setValue(tableField.descriptor.metadatas.typeOfItems);
              workCycleTableType.value.value = tableField.descriptor.metadatas.typeOfItems;
            }
            tableField.descriptor.metadatas.endDate = endDateValue;
            tableField.changeDetectorRef.detectChanges();
          }),
          takeUntil(endCycleField.componentDestroyed$)
        )
        .subscribe();
    }

    refDateField.control.valueChanges
      .pipe(
        takeUntil(refDateField.componentDestroyed$)
      )
      .subscribe(refDateValue => {
        if (workCycleField.value.value === PERSONAL_WORKCYCLE_ID) {
          personalCycle.refdate = refDateValue || '';
          tableField.descriptor.metadatas.typeOfItems = fillTypeOfItems(refDateValue);
          workCycleTableType.control.setValue(tableField.descriptor.metadatas.typeOfItems);
          workCycleTableType.value.value = tableField.descriptor.metadatas.typeOfItems;
          tableField.descriptor.metadatas.endDate = endCycleField.value.value;
        }
        tableField.descriptor.metadatas.startDate = refDateValue;
        tableField.changeDetectorRef.detectChanges();
      });

    workCycleField.control.valueChanges
      .pipe(
        takeUntil(workCycleField.componentDestroyed$),
        startWith(workCycleField.control.value),
        debounceTime(100)
      )
      .subscribe((value: string) => {
        const cycleMetadata = this.descriptor.metadatas.items.find((item: any) => item.cycleId === value);

        if (cycleMetadata) {
          let isReadOnly: boolean = true;
          const tableValues: number[] = [...cycleMetadata.items];
          if (cycleMetadata.cycleId === PERSONAL_WORKCYCLE_ID) {
            tableValues.length = 365;
            isReadOnly = false;
          }
          tableField.control.setValue(tableValues);
          tableField.value.value = tableField.control.value;
          tableField.descriptor.metadatas.typeOfItems = cycleMetadata.typeOfItems;

          workCycleTableType.control.setValue(tableField.descriptor.metadatas.typeOfItems);
          workCycleTableType.value.value = tableField.descriptor.metadatas.typeOfItems;

          refDateField.control.setValue(cycleMetadata.refdate);
          refDateField.value.value = refDateField.control.value;

          if (tableField.componentRef) {
            tableField.componentRef.instance.readOnly = isReadOnly;
          }

          if (refDateField.componentRef) {
            refDateField.componentRef.instance.readOnly = isReadOnly;
          }

          workCycleType.value.value = isReadOnly ? 'common' : 'personal';
          workCycleType.control.setValue(workCycleType.value.value);

          isReadOnly ? tableField.control.disable() : tableField.control.enable();
          isReadOnly ? refDateField.control.disable() : refDateField.control.enable();

          tableField.control.updateValueAndValidity();
          if (!tableField.isDestroyed) {
            tableField.changeDetectorRef.detectChanges();
          }

          refDateField.control.updateValueAndValidity();
          if (!refDateField.isDestroyed) {
            refDateField.changeDetectorRef.detectChanges();
          }

          this.group.updateValueAndValidity();
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  @HostBinding('class')
  public get cardClass() {
    return this.cardCls;
  }

  private allDataValuesAreEmpty(values: ZoneValues) {
    if (values) {
      return Object.values(values)
        .every(value => isValueConsideredAsEmpty(value.value));
    }
  }

  private noDataForThisGroup() {
    if (this.data) {
      return this.data.every(data => !data.id);
    }
  }

  private allCardsValuesAreEmpty() {
    if (this.data) {
      return this.data.every(data => this.allDataValuesAreEmpty(data.values));
    }
  }

  private isUndefinedDataEmpty() {
    if (this.group) {
      return this.noDataForThisGroup() && this.allCardsValuesAreEmpty();
    }
  }

  private isDataChanged() {
    for (let i = 0; i < this.data.length; i++) {
      if (this.data[i].begindate !== this.initialData[i].begindate
        || this.data[i].enddate !== this.initialData[i].enddate) {
        return true;
      }
      const dataValues: Value[] = Object.values(this.data[i].values);
      const initialValues: Value[] = Object.values(this.initialData[i].values);

      if (dataValues.length !== initialValues.length
        || dataValues.some((dataValue, index) => !initialValues[index] || dataValue.value !== initialValues[index].value)) {
        return true;
      }
    }
    return false;
  }

  private getCardElements(): CardElementErrors[] {
    const cardElementsErrors = [];

    this.data.forEach(data => {
      this.groupCopy.controls.beginDateControl.setValue(data.begindate);
      this.groupCopy.controls.endDateControl.setValue(data.enddate);
      const zoneErrors = [];
      Object.entries(data.values).forEach((zone: any) => {
        const key = FormGeneratorUtils.sanitizeZoneName(zone[0]);
        const value = zone[1].value;
        if (this.groupCopy.controls.hasOwnProperty(key)) {
          this.groupCopy.controls[key].setValue(value, {emitEvent: false});
          if (this.groupCopy.controls[key].errors) {
            Object.keys(this.groupCopy.controls[key].errors).forEach(error => {
              if (this.descriptor.groupZoneID === 'WorkCycle' && key === 'WorkCycle' && error === 'valueNotInList') {
                if (!this.isWorkCyclePersonalWithValue(value, data.values['workCycleType'].value)) {
                  zoneErrors.push(error);
                }
              } else {
                zoneErrors.push(error);
              }
            });
          }
        }
      });
      cardElementsErrors.push({index: data.dataIndex, errors: zoneErrors});
    });

    return cardElementsErrors;
  }

  private getAllElementsWithErrors(cardElementsErrors: CardElementErrors[]) {

    const elementsWithErrors = [];

    cardElementsErrors.forEach(group => {
      let isBlockingError = false;
      if (group.errors.length > 0) {
        if (group.errors.some(error =>
          this.formGeneratorService.isErrorBlocking(this.formGeneratorService.formatErrorKey(error) as FormGeneratorErrorKind))) {

          isBlockingError = true;
        }
        elementsWithErrors.push({index: group.index, isBlocking: isBlockingError});
      }
    });

    return elementsWithErrors;
  }

  private checkZoneErrorsInNonActiveEntities() {
    if (this.data.length > 1 && this.descriptor.isDated) {

      const cardElements = this.getCardElements();
      const elementsWithErrors = this.getAllElementsWithErrors(cardElements);
      const allElementsAreValid = elementsWithErrors.length <= 0;

      if (!allElementsAreValid) {
        const activeEntityWithoutErrors =
          elementsWithErrors.every(element => element.index !== this.activeEntity.dataIndex);

        const activeEntityWithoutBlockingErrors = !activeEntityWithoutErrors ?
          !elementsWithErrors.find(element => element.index === this.activeEntity.dataIndex).isBlocking : true;

        if (elementsWithErrors.some(element => element.isBlocking)) {
          if (activeEntityWithoutErrors || activeEntityWithoutBlockingErrors) {
            this.pushErrorToErrorGroup(this.groupErrors, FormGeneratorErrorKind.BLOCKING_ERROR_ON_ANOTHER_ELEMENT);
          }
        } else {
          if (activeEntityWithoutErrors) {
            this.pushErrorToErrorGroup(this.groupErrors, FormGeneratorErrorKind.NON_BLOCKING_ERROR_ON_ANOTHER_ELEMENT);
          }
        }

      }
    }
  }

  private isWorkCyclePersonalWithValue(value: any, workCycleType: string) {
    const cycle = this.descriptor.metadatas.items.find((item: any) => item.cycleId === value);
    return !cycle && value && workCycleType === 'personal';
  }

  private deleteDatedCardEntity() {
    this.modalService.dynamicConfirmModal({
      title: 'DIALOG.delete_block_occurrence',
      text: this.translate.instant('DIALOG.delete_block_occurrence?', {blockName: this.descriptor.transId}),
      confirmButton: 'FORMS.delete_occurrence',
      cancelButton: 'FORMS.cancel'
    }).then((choice: boolean) => {
      if (choice) {
        if (this.data.length < 2) {
          this.clearBlock();
        } else {
          this.deleteEntityHandler()
            .then(() => {
              this.data.splice(this.dataIndex, 1);
              if (this.dataIndex) {
                this.dataIndex--;
              }
              this.updateGroupStatus();
              this.loadFields();
            });
        }
      }
    });
  }

  private deleteNonDatedCardEntity() {
    this.modalService.dynamicConfirmModal({
      title: 'DIALOG.delete_block_occurrence',
      text: this.translate.instant('DIALOG.delete_block_occurrence?', {blockName: this.getBlockNameByInternalType()}),
      confirmButton: 'FORMS.delete_occurrence',
      cancelButton: 'FORMS.cancel'
    }).then((choice: boolean) => {
      if (choice) {
        this.clearBlock();
      }
    });
  }

  private clearBlock() {
    this.data[0] = this.formGeneratorService.createEmptyData(this.descriptor, undefined, true)[0];
    this.updateGroupStatus();
    this.loadFields();
  }

  private getBlockNameByInternalType(): string {
    return this.descriptor.internalType === 'BANKING_DETAILS'
      ? this.descriptor.transId + ' - ' + this.translate.instant(this.descriptor.groupZoneID)
      : this.descriptor.transId;
  }

  public setAiGeneratedZones() {
    if (this.selectedOrg?.attributes?.tree === 'Jobs' && !this.descriptor.isDated) {
      this.aiGeneratedZones = this.descriptor?.zones
        .filter((zone: ZoneDescriptor) => zone?.arg1 === CAN_GENERATE_WITH_AI && (zone.type === ZoneType.TEXT || zone.type === ZoneType.LABEL))
        .map((zone: ZoneDescriptor) => {
          const arg2Array = zone.arg2.split(',').map(s => s.trim());
          arg2Array.forEach(groupZoneId => {
            if (!this.arg2GroupZonesForAiContext.includes(groupZoneId)) {
              this.arg2GroupZonesForAiContext.push(groupZoneId);
            }
          });
          return zone.zoneID;
        }) || [];
    }
  }

  public generateZoneValuesWithAI(): void {
    const sources: AiSourceOrTarget[] = this.buildSources(this.arg2GroupZonesForAiContext);
    if (sources.length < 2) {
      return;
    }

    const target: AiSourceOrTarget = this.buildTarget();

    // Todo: Replace with proper block identifier during implementation with configuration
    const contentGenerationIdentifier = 'JOB_PROFILE';

    const targetZoneIds = this.getAiTargetZoneIds();

    this.setAiProcessing({
      processing: true,
      targetZoneIds
    }, 'aiResponseLoading');

    this.chatbotService.generateWithAI(sources, target, contentGenerationIdentifier).pipe(
      tap((resp: AiGeneratedValues) => this.applyAITargetResponse(resp)),
      finalize(() => this.setAiProcessing({processing: false}, 'aiResponseLoading')),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private getAiTargetZoneIds(): string[] {
    if (!this.descriptor || !this.group) {
      return [];
    }

    const disabledTargets = new Set(
      (this.descriptor.dependencies ?? [])
        .filter(dependency => dependency.action === 'SET_DISABLED' && dependency.actionValue === 'true')
        .map(dependency => dependency.target)
    );

    return this.aiGeneratedZones
      .filter(zoneId => !disabledTargets.has(zoneId));
  }


  private buildSources(arg2GroupZones: string[]): AiSourceOrTarget[] {
    const sources: AiSourceOrTarget[] = [{
      ['Jobs.Title']: this.selectedOrg.attributes.name
    }];

    arg2GroupZones.forEach(groupZoneId => {
      const formValues = this.allData.get(groupZoneId);
      if (formValues?.length) {
        const latestFormValue = formValues.reduce((latest, current) =>
          moment(current.enddate).isAfter(latest.enddate) ? current : latest
        );

        const source: AiSourceOrTarget = {
          blockName: groupZoneId,
        };
        Object.keys(latestFormValue.values).forEach(zoneId => {
          const zone = latestFormValue.values[zoneId];
          source[zoneId] = zone.transId ?? zone.value;
        });

        sources.push(source);
      }
    });

    return sources;
  }

  public applyAITargetResponse(resp): void {
    const targetValues: AiSourceOrTarget = resp?.target;
    if (!targetValues) {
      return;
    }

    const blockName = targetValues.blockName;

    Object.entries(targetValues)
      .filter(([key]) => key !== 'blockName')
      .filter(([key]) => !(blockName.startsWith('BankingDetails') && key.endsWith('.paymentModality')))
      .forEach(([zoneId, value]) => {
        const ctrlKey = FormGeneratorUtils.sanitizeZoneName(zoneId);
        const ctrl = this.group?.get(ctrlKey) as AbstractControl | null;
        if (ctrl) {
          ctrl.setValue(value);
          ctrl.markAsDirty();
          ctrl.updateValueAndValidity({emitEvent: true});
        }
      });

    this.changeDetectorRef.markForCheck();
  }

  public applyPrefillResponse(resp: PrefillResponseDTO): void {
    const groups = resp?.groups ?? [];
    if (!groups.length || !this.descriptor) {
      return;
    }

    const groupResponse = groups.find(group => group.groupId === this.descriptor.groupZoneID);
    if (!groupResponse) {
      return;
    }

    Object.entries(groupResponse.fields ?? {})
      .forEach(([zoneId, fieldValue]) => {
        const ctrlKey = FormGeneratorUtils.sanitizeZoneName(zoneId);
        const ctrl = this.group?.get(ctrlKey) as AbstractControl | null;
        if (ctrl && fieldValue?.value != null) {
          ctrl.setValue(fieldValue.value);
          ctrl.markAsDirty();
          ctrl.updateValueAndValidity({emitEvent: true});
        }
      });

    this.changeDetectorRef.markForCheck();
  }

  public toggleAIDisclaimer() {
    this.showAiDisclaimer = true;
  }

  private onGenerateWithAiUsingFile(
    empFile: EmpFile,
    event?: MouseEvent
  ): void {
    event?.stopPropagation();

    this.changeDetectorRef.markForCheck();

    const request = this.buildPrefillRequest();
    const targetZoneIds = request.groups.reduce((ids: string[], group) => {
      group.fields.forEach(field => ids.push(field.fieldId));
      return ids;
    }, []);

    this.setAiProcessing({processing: true, targetZoneIds}, 'aiOCRResponseLoading');
    this.getFile(empFile)
      .pipe(
        switchMap((file: File) =>
          this.chatbotService.generateWithAIUsingOCR(file, request)
        ),
        tap((response) => {
          this.applyPrefillResponse(response);
        }),
        finalize(() => {
          this.changeDetectorRef.markForCheck();

          this.setAiProcessing({processing: false}, 'aiOCRResponseLoading');
          this.toggleAIDisclaimer();
        })
      )
      .subscribe();
  }

  private buildTarget(): AiSourceOrTarget {
    const targetFields: AiSourceOrTarget = {blockName: this.descriptor?.groupZoneID || ''};

    const disabledTargets = new Set(
      (this.descriptor.dependencies)
        .filter(dependency => dependency.action === 'SET_DISABLED' && dependency.actionValue === 'true')
        .map(dependency => dependency.target.split('_', 1)[0])
    );

    this.descriptor.zones
      .filter(zone =>
        !zone.zoneID.endsWith('paymentModality') &&
        !zone.hidden &&
        !zone.readOnly &&
        zone.type !== 'FILE' &&
        zone.type !== 'BOOLEAN' &&
        !disabledTargets.has(zone.zoneID)
      )
      .forEach(zone => (targetFields[zone.zoneID] = zone.transId));
    return targetFields;
  }

  private buildPrefillRequest(): PrefillRequestDTO {
    const request: PrefillRequestDTO = {
      groups: [{
        groupId: this.descriptor?.groupZoneID || '',
        groupTransId: this.descriptor?.transId ?? (this.descriptor?.groupZoneID || ''),
        fields: []
      }]
    };

    if (this.cliRegID != null) {
      request.regulationId = this.cliRegID;
    }

    const disabledTargets = new Set(
      (this.descriptor?.dependencies ?? [])
        .filter(dependency => dependency.action === 'SET_DISABLED' && dependency.actionValue === 'true')
        .map(dependency => dependency.target.split('_', 1)[0])
    );

    request.groups[0].fields = (this.descriptor?.zones ?? [])
      .filter(zone =>
        !zone.zoneID.endsWith('paymentModality') &&
        !zone.hidden &&
        !zone.readOnly &&
        zone.type !== 'FILE' &&
        zone.type !== 'BOOLEAN' &&
        !disabledTargets.has(zone.zoneID)
      )
      .map(zone => ({
        fieldId: zone.zoneID,
        transId: zone.transId,
        type: zone.type,
        isRequired: !!zone.isRequired
      }));

    return request;
  }

  private getFile(empFile: EmpFile): Observable<File> {
    return this.employeeService
      .getFile(
        empFile,
        this.descriptor?.metadatas?.downloadFileUrl,
        this.descriptor?.metadatas?.fileUrlsAuthenticated,
        ProfileMode.CLASSIC
      )
      .pipe(
        take(1),
        map((fileModel: FileModel) => {
          return new File(
            [fileModel.blob],
            fileModel.fileName,
            {type: fileModel.blob.type}
          );
        }),
        catchError((err) => {
          return throwError(() => err);
        })
      );
  }

  private setAiProcessing(aiProcessingEvent: AiProcessingEvent, action: string): void {
    this.employeeService.setEmployeeFileActionButtonsDisabled({action, value: aiProcessingEvent.processing});
    this.isGroupZoneLocked = aiProcessingEvent.processing;

    if (aiProcessingEvent.processing) {
      this.lockZones(aiProcessingEvent.targetZoneIds ?? []);
    } else {
      this.unlockZones();
    }
  }

  private lockZones(zoneIds: string[]): void {
    zoneIds
      .map(zoneId => FormGeneratorUtils.sanitizeZoneName(zoneId))
      .forEach(ctrlKey => {
        const ctrl = this.group?.get(ctrlKey);
        if (!ctrl) {
          return;
        }

        if (ctrl.enabled) {
          ctrl.disable({emitEvent: false});
          this.aiLockedControls.add(ctrlKey);
        }
      });

    this.changeDetectorRef.markForCheck();
  }

  private unlockZones(): void {
    this.aiLockedControls.forEach(ctrlKey => {
      this.group?.get(ctrlKey)?.enable({emitEvent: false});
    });
    this.aiLockedControls.clear();

    this.group?.updateValueAndValidity({emitEvent: false});
    this.changeDetectorRef.markForCheck();
  }

  private registerDependencies() {
    if (this.readOnly) {
      return;
    }
    this.dependencySubscriptions?.forEach(subscription => subscription.unsubscribe());
    this.dependencySubscriptions = this.empZoneDependencyUtils.registerDependenciesOnOneGroup(
      this.descriptor,
      this.group,
      target => this.findDependencyTriggers(target)
    )
      ?.map(dependencyObservable => dependencyObservable.pipe(takeUntil(this.componentDestroyed$)))
      .map(dependencyObservable => dependencyObservable.subscribe());
  }

  private findDependencyTriggers(target: string): BehaviorSubject<EmpZoneDependency>[] {
    return this.fieldRows
      ?.flatMap(fieldRow => fieldRow.instance.fields)
      .map(field => field.instance)
      .filter(instance => instance.descriptor.zoneID === target
        || instance.descriptor.zoneID === FormGeneratorUtils.simplifyZoneName(target))
      .map(instance => instance.dependencyTriggered$);
  }
}
